/**
 * GroupPhoto — single full-width workshop group photo above the participant list.
 *
 * Reads URL + alt from report_edits via useReportEdits (field keys
 * `group_photo.image_url` and `group_photo.alt`, populated per instance by
 * apps/backend/src/scripts/populate-group-photo.js). When no URL is set the
 * component renders nothing — additive, safe in prod even before the photo
 * is uploaded.
 *
 * Click opens the shared ImageLightbox (same component used by
 * Mitä jos? and Tulevaisuuskuvat). Single-image mode → no chevrons.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useReportEdits } from './ReportEditsContext';
import ImageLightbox from './ImageLightbox';
import { normalizeImageUrl } from './imageUrl';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Frame = styled.button`
  display: block;
  width: 100%;
  margin: 24px 0 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  appearance: none;
  font: inherit;
  color: inherit;
  text-align: left;

  img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 4px;
  }
`;

export default function GroupPhoto() {
  const { get } = useReportEdits();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const url = normalizeImageUrl(get('group_photo.image_url', ''));
  if (!url) return null;

  const alt = get('group_photo.alt', t('report.groupPhoto.defaultAlt'));

  return (
    <>
      <Frame onClick={() => setOpen(true)} aria-label={t('report.groupPhoto.openAriaLabel')}>
        <img src={url} alt={alt} />
      </Frame>

      {open && (
        <ImageLightbox
          images={[{ url, alt, caption: alt }]}
          initialIndex={0}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
